import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { LegalOnboardingStatus } from "@/constants/entity-status";
import { AppRoutes } from "@/constants/routes";
import { PermissionCode } from "@/constants/permissions";
import { useRoles } from "@/hooks/useRoles";
import { Flex, useDisclosure } from "@chakra-ui/react";
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";

import "@/components/common/section-components/card/text-elements.css";

import { HeaderActions } from "./components/HeaderActions";
import { LegalAccountDetailCard } from "./components/LegalAccountDetailCard";
import { RequestingUserDetailCard } from "./components/RequestingUserDetailCard";
import { DocumentsList } from "./components/DocumentsList";
import { OtrasDdjjCard } from "./components/OtrasDdjjCard";
import {
  DocDecision,
  LegalOnboardingDetail,
  fetchAndMapLegalOnboardingDetailHandler,
  startReviewLegalOnboardingHandler,
} from "./schema/handler-functions";
import { useApproveFlow } from "./flows/useApproveFlow";
import { useRequireChangesFlow } from "./flows/useRequireChangesFlow";
import {
  showApproveErrorToast,
  showApproveSuccessToast,
  showMustStartReviewToast,
  showRequireChangesSuccessToast,
  showStartReviewSuccessToast,
} from "./flows/toaster-functions";
import { OBJValidationsCard } from "./components/OBJValidationsCard";
import { getDocumentDecisionSummary } from "./schema/document-decision-summary";

const RequireChangesModalDynamic = lazy(() =>
  import("./components/RequireChangesModal").then((el) => ({
    default: el["RequireChangesModal"],
  })),
);

const DomicilioModalDynamic = lazy(() =>
  import("./components/DomicilioModal").then((el) => ({
    default: el["DomicilioModal"],
  })),
);

const ApproveModalDynamic = lazy(() =>
  import("./components/ApproveModal").then((el) => ({
    default: el["ApproveModal"],
  })),
);

const ControlLogModalDynamic = lazy(() =>
  import("./components/ControlLogModal").then((el) => ({
    default: el["ControlLogModal"],
  })),
);

export const LegalOnboardingDetailPage = () => {
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    setIsActive(true);
  }, []);
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { fetchPermissions, userPermissions } = useRoles();
  const lastMustStartReviewToastAtRef = useRef<number>(0);

  const [loadingDetail, setLoadingDetail] = useState<boolean>(false);
  const [detail, setDetail] = useState<LegalOnboardingDetail | null>(null);
  const [reviewStarted, setReviewStarted] = useState<boolean>(false);
  const [decisions, setDecisions] = useState<Record<number, DocDecision>>({});
  const [loadingStartReview, setLoadingStartReview] = useState<boolean>(false);
  const { open: openControlLog, setOpen: setOpenControlLog } = useDisclosure();

  const listRouteState = useMemo(() => {
    const state = location.state as { page?: number } | null;
    const page = state?.page;
    return page ? { page } : undefined;
  }, [location.state]);

  useEffect(() => {
    let cancelled = false;
    if (isActive && id) {
      if (!id) return;
      const numericId = Number(id);
      if (!numericId) return;

      fetchAndMapLegalOnboardingDetailHandler({
        id: numericId,
        setLoadingDetail,
        setDetail,
        setDecisions,
        setReviewStarted,
        cancelled: () => cancelled,
        navigate,
        fetchPermissions,
        routeState: listRouteState,
      });
    }

    return () => {
      cancelled = true;
    };
  }, [id, isActive, listRouteState]);

  const onShowMustStartReviewToast = () =>
    showMustStartReviewToast(lastMustStartReviewToastAtRef);

  const isPendingStatus = detail?.estado === LegalOnboardingStatus.PENDING;
  const isPreloadedStatus = detail?.estado === LegalOnboardingStatus.PRELOADED;
  const isApprovedStatus = detail?.estado === LegalOnboardingStatus.APPROVED;

  const isRequiresModifStatus =
    detail?.estado === LegalOnboardingStatus.REQUIRES_MODIF;

  const canWriteLegalOnboarding = useMemo(() => {
    return (userPermissions ?? []).some(
      (p) => p?.codigo === PermissionCode.LEGAL_ONBOARDING && p?.tipoPermiso === "W",
    );
  }, [userPermissions]);

  const { allAccepted, hasRejected, hasUndecided } = useMemo(
    () => getDocumentDecisionSummary(detail?.documentos, decisions),
    [detail?.documentos, decisions],
  );

  const approveDisabled = !reviewStarted || !allAccepted;
  const requireChangesDisabled =
    isPendingStatus ||
    isApprovedStatus ||
    isRequiresModifStatus ||
    !reviewStarted ||
    hasUndecided ||
    !hasRejected;

  const {
    register,
    reset,
    formState: { errors },
    getValues,
    watch,
  } = useForm();

  const refreshDetail = async () => {
    if (!id) return;
    const numericId = Number(id);
    if (!numericId) return;
    await fetchAndMapLegalOnboardingDetailHandler({
      id: numericId,
      setLoadingDetail,
      withLoading: false,
      setDetail,
      setDecisions,
      setReviewStarted,
      navigate,
      fetchPermissions,
      routeState: listRouteState,
    });
  };

  const onBack = () => {
    navigate(AppRoutes.LEGAL_ONBOARDING, {
      state: listRouteState,
    });
  };

  const approveFlow = useApproveFlow({
    id,
    detail,
    decisions,
    navigate,
    fetchPermissions,
    routeState: listRouteState,
    onBack,
    showApproveSuccessToast,
    showApproveErrorToast,
  });

  const requireChangesFlow = useRequireChangesFlow({
    id,
    detail,
    decisions,
    navigate,
    fetchPermissions,
    routeState: listRouteState,
    getValues,
    reset,
    watch,
    showMustStartReviewToast: onShowMustStartReviewToast,
    showRequireChangesSuccessToast,
    refreshDetail,
    requireChangesDisabled,
  });

  const handleOnStartReview = async () => {
    if (!isPendingStatus || !id) return;
    try {
      setLoadingStartReview(true);
      await startReviewLegalOnboardingHandler({
        id: Number(id),
        navigate,
        fetchPermissions,
        routeState: listRouteState,
      });
      showStartReviewSuccessToast();
      await refreshDetail();
    } catch {
      // handler already handles permission + error toast
    } finally {
      setLoadingStartReview(false);
    }
  };

  const topRightButtons = (
    <HeaderActions
      isPendingStatus={isPendingStatus}
      loadingStartReview={loadingStartReview}
      approveDisabled={approveDisabled}
      requireChangesDisabled={requireChangesDisabled}
      showApprove={canWriteLegalOnboarding && !isApprovedStatus}
      showRequireChanges={
        canWriteLegalOnboarding && !isApprovedStatus && Boolean(detail?.estado)
      }
      onStartReview={handleOnStartReview}
      onApprove={approveFlow.onApprove}
      onRequireChanges={requireChangesFlow.onRequireChanges}
      onBack={onBack}
    />
  );

  const backOnlyButtons = (
    <HeaderActions
      isPendingStatus={isPendingStatus}
      loadingStartReview={loadingStartReview}
      approveDisabled={approveDisabled}
      requireChangesDisabled={requireChangesDisabled}
      showApprove={false}
      showRequireChanges={false}
      onStartReview={() => undefined}
      onApprove={() => undefined}
      onRequireChanges={() => undefined}
      onBack={onBack}
    />
  );

  const headerButtons = isPreloadedStatus ? backOnlyButtons : topRightButtons;

  return (
    <Flex
      flexDir="column"
      paddingTop="20px"
      gap="12px"
      height="100%"
      minHeight={0}
    >
      <SectionHeader
        title="Onboarding Jurídico"
        loading={loadingDetail}
        aditionalButtons={headerButtons}
      />

      <Suspense>
        <RequireChangesModalDynamic
          open={requireChangesFlow.openRequireChanges}
          onOpenChange={requireChangesFlow.onOpenChangeRequireChanges}
          loading={requireChangesFlow.loadingRequireChanges}
          errors={errors}
          register={register}
          value={requireChangesFlow.requireChangesDetail ?? ""}
          onConfirm={requireChangesFlow.onConfirmRequireChanges}
        />

        <DomicilioModalDynamic
          open={approveFlow.openDomicilio}
          onOpenChange={approveFlow.onOpenChangeDomicilio}
          loadingConfirm={approveFlow.loadingBind}
          onCancel={approveFlow.onCancelDomicilio}
          onConfirm={approveFlow.onConfirmDomicilio}
          initialDomicilio={approveFlow.domicilio}
        />

        <ApproveModalDynamic
          open={approveFlow.openApprove}
          onOpenChange={approveFlow.onOpenChangeApprove}
          loadingBind={approveFlow.loadingBind}
          loadingConfirm={approveFlow.loadingConfirmApprove}
          canRetry={approveFlow.canRetry}
          bindData={approveFlow.bindData}
          onRetryBind={approveFlow.onRetryBind}
          onCancel={approveFlow.onCancelApprove}
          onBack={approveFlow.onBackToDomicilio}
          onConfirm={approveFlow.onConfirmApprove}
        />

        <ControlLogModalDynamic
          open={openControlLog}
          onOpenChange={(e) => setOpenControlLog(e.open)}
          detail={detail}
        />
      </Suspense>

      {!loadingDetail && (
        <Flex
          flex="1"
          minHeight={0}
          overflowY="auto"
          flexDir="column"
          gap="16px"
        >
          <LegalAccountDetailCard detail={detail} />
          <RequestingUserDetailCard detail={detail} />

          <DocumentsList
            docs={detail?.documentos ?? []}
            decisions={decisions}
            setDecisions={setDecisions}
            reviewStarted={reviewStarted}
            isPendingRequest={isPendingStatus}
            isApprovedRequest={isApprovedStatus}
            isPreloadedRequest={isPreloadedStatus}
            isRequiresModifRequest={isRequiresModifStatus}
            canWrite={canWriteLegalOnboarding}
            showMustStartReviewToast={onShowMustStartReviewToast}
          />

          <OtrasDdjjCard detail={detail} />

          <OBJValidationsCard
            detail={detail}
            onOpenControlLog={() => setOpenControlLog(true)}
          />
        </Flex>
      )}
    </Flex>
  );
};
